"use server"

import { prisma } from "@/lib/prisma"
import { getBaseUrl } from "@/lib/base-url"
import { sendEmail } from "@/lib/services/email.service"
import { emailVerificationService } from "@/lib/services/email-verification.service"
import { registerSchema } from "@/lib/schemas/auth.schema"
import { cguAcceptance } from "@/lib/legal"

// Donnees brutes du formulaire (acceptCgu peut etre false ; la validation
// via registerSchema impose qu'il soit true).
type RegisterFormInput = {
  firstName: string
  lastName: string
  email: string
  acceptCgu: boolean
}

type RegisterResult =
  | { ok: true }
  | { ok: false; error: string; fieldErrors?: Record<string, string[]> }

async function sendVerificationEmail(
  userId: string,
  email: string,
  firstName: string,
): Promise<void> {
  const token = await emailVerificationService.create(userId)
  const baseUrl = await getBaseUrl()
  const verifyUrl = `${baseUrl}/finalize-registration?token=${encodeURIComponent(token)}`

  const text = [
    `Bonjour ${firstName},`,
    "",
    "Bienvenue sur archifacil ! Pour finaliser votre inscription et",
    "définir votre mot de passe, cliquez sur le lien ci-dessous :",
    "",
    verifyUrl,
    "",
    "Ce lien est valable 24 heures. Si vous n'êtes pas à l'origine de cette",
    "inscription, ignorez simplement cet email.",
    "",
    "-- ",
    "archifacil — Architecture & plans électriques",
  ].join("\n")

  await sendEmail({
    to: email,
    subject: "Confirmez votre inscription à archifacil",
    text,
  })
}

export async function registerAction(
  input: RegisterFormInput,
): Promise<RegisterResult> {
  const parsed = registerSchema.safeParse(input)

  if (!parsed.success) {
    const fieldErrors: Record<string, string[]> = {}
    for (const issue of parsed.error.issues) {
      const key = String(issue.path[0])
      if (!fieldErrors[key]) fieldErrors[key] = []
      fieldErrors[key].push(issue.message)
    }
    return { ok: false, error: "Données invalides", fieldErrors }
  }

  const { firstName, lastName, email } = parsed.data

  const existing = await prisma.user.findUnique({
    where: { email },
    select: { id: true, password: true, emailVerified: true },
  })

  if (existing) {
    // Compte deja finalise (mot de passe defini ou email verifie) ou compte
    // Google : on refuse la re-inscription.
    if (existing.password || existing.emailVerified) {
      return {
        ok: false,
        error: "Un compte existe déjà avec cet email",
        fieldErrors: { email: ["Un compte existe déjà avec cet email"] },
      }
    }

    // Compte en attente de verification : on renvoie simplement l'email et on
    // met a jour la date d'acceptation des CGU.
    await prisma.user.update({
      where: { id: existing.id },
      data: { firstName, lastName, name: `${firstName} ${lastName}`, ...cguAcceptance() },
    })
    await sendVerificationEmail(existing.id, email, firstName)
    return { ok: true }
  }

  // Nouveau compte "en attente" : pas de mot de passe, email non verifie.
  const user = await prisma.user.create({
    data: {
      email,
      firstName,
      lastName,
      name: `${firstName} ${lastName}`,
      ...cguAcceptance(),
    },
    select: { id: true },
  })

  await sendVerificationEmail(user.id, email, firstName)

  return { ok: true }
}
